import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PropTypes from 'prop-types';
import { TranslationCreateTranslation } from './services';
import { MdOutlineSaveAlt } from 'react-icons/md';
import { toast } from 'react-toastify';

const TranslationPluginForm = ({ languageId, onSuccess, onError }) => {
  const [transKey, setTransKey] = useState('');
  const [transValue, setTransValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [jsonFile, setJsonFile] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const resetForm = () => {
    setTransKey('');
    setTransValue('');
    setSuccess('');
    setError('');
    setCsvFile(null);
    setJsonFile(null);
  };

  const handleCreateTranslation = async (payload) => {
    try {
      await TranslationCreateTranslation(payload);
      return true;
    } catch (err) {
      throw new Error(err?.response?.data?.message || 'Failed to create translation');
    }
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (!transKey.trim() || !transValue.trim()) {
      setError('Both Trans Key and Trans Value are required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await handleCreateTranslation([{ lid: languageId, transKey, transValue }]);
      setSuccess(`Translation "${transKey}" created successfully`);
      resetForm();
      onSuccess?.();
    } catch (err) {
      setError(err.message);
      onError?.(err.message);
    } finally {
      setLoading(false);
    }
  };

  const parseCSV = (fileContent) => {
    const lines = fileContent.trim().split('\n');
    const translations = [];

    const removeQuotes = (value) => {
      const trimmed = value.trim();
      // Remove surrounding quotes (single or double) and handle escaped quotes
      if (
        (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
        (trimmed.startsWith("'") && trimmed.endsWith("'"))
      ) {
        return trimmed.slice(1, -1).replace(/\\"/g, '"').replace(/\\'/g, "'");
      }
      return trimmed;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      // Support both comma and tab separated values
      let parts;
      if (line.includes('\t')) {
        parts = line.split('\t');
      } else {
        // Simple CSV parsing that handles quoted values
        const csvParts = [];
        let current = '';
        let inQuotes = false;
        const quoteChar = line[0] === '"' || line[0] === "'" ? line[0] : null;

        for (let j = 0; j < line.length; j++) {
          const char = line[j];

          if (char === '"' || char === "'") {
            if (!inQuotes && current.trim() === '') {
              inQuotes = true;
            } else if (char === quoteChar && inQuotes) {
              inQuotes = false;
            } else {
              current += char;
            }
          } else if (char === ',' && !inQuotes) {
            // Check if comma is escaped with backslash
            const isEscaped = current.endsWith('\\');
            if (isEscaped) {
              // Remove the backslash and keep the comma as part of the value
              current = current.slice(0, -1) + ',';
            } else {
              csvParts.push(current);
              current = '';
            }
          } else {
            current += char;
          }
        }
        if (current) {
          csvParts.push(current);
        }
        parts = csvParts;
      }

      const key = removeQuotes(parts[0]);
      const value = removeQuotes(parts?.[1] || '');

      if (key && value) {
        translations.push({ transKey: key, transValue: value, lid: languageId });
      }
    }

    if (translations.length === 0) {
      throw new Error('No valid translations found in the CSV file');
    }

    return translations;
  };

  const parseJSON = (fileContent) => {
    const translations = [];
    const data = JSON.parse(fileContent);

    // Handle array format: [{ transKey: "...", transValue: "..." }, ...]
    if (Array.isArray(data)) {
      for (const item of data) {
        if (item.transKey && item.transValue) {
          translations.push({
            transKey: item.transKey,
            transValue: item.transValue,
            lid: languageId,
          });
        }
      }
    }
    // Handle object format: { key: "value", ... }
    else if (typeof data === 'object' && data !== null) {
      for (const [key, value] of Object.entries(data)) {
        if (key && value) {
          translations.push({
            transKey: key,
            transValue: String(value),
            lid: languageId,
          });
        }
      }
    } else {
      throw new Error('Invalid JSON format. Expected an array or object.');
    }

    if (translations.length === 0) {
      throw new Error('No valid translations found in the JSON file');
    }

    return translations;
  };

  const handleCSVSubmit = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      setError('Please select a CSV file');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const fileContent = await csvFile.text();
      const translations = parseCSV(fileContent)?.map((t) => ({ ...t, lid: languageId }));
      console.log('Parsed Translations:', translations);
      let successCount = 0;
      const errors = [];

      try {
        await handleCreateTranslation(translations);
        successCount++;
      } catch (err) {
        toast.error(`Couldn't create translations.`);
      }

      if (errors.length === 0) {
        setSuccess(`All ${successCount} translations created successfully`);
      } else {
        setSuccess(
          `${successCount} translations created successfully, but ${errors.length} failed`
        );
        setError(`Failed translations:\n${errors.join('\n')}`);
      }

      resetForm();
      onSuccess?.();
    } catch (err) {
      setError(err.message);
      onError?.(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleJSONSubmit = async (e) => {
    e.preventDefault();
    if (!jsonFile) {
      setError('Please select a JSON file');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const fileContent = await jsonFile.text();
      const translations = parseJSON(fileContent);
      console.log('Parsed Translations:', translations);
      let successCount = 0;
      const errors = [];

      try {
        await handleCreateTranslation(translations);
        successCount++;
      } catch (err) {
        toast.error(`Couldn't create translations.`);
      }

      if (errors.length === 0) {
        setSuccess(`All ${successCount} translations created successfully`);
      } else {
        setSuccess(
          `${successCount} translations created successfully, but ${errors.length} failed`
        );
        setError(`Failed translations:\n${errors.join('\n')}`);
      }

      resetForm();
      onSuccess?.();
    } catch (err) {
      setError(err.message);
      onError?.(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component={Paper} sx={{ p: 3, mt: 3 }}>
      {success && (
        <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        sx={{ mb: 3 }}
      >
        <Tab label='Single Translation' />
        <Tab label='Upload CSV' />
        <Tab label='Upload JSON' />
      </Tabs>

      {/* Single Translation Tab */}
      {activeTab === 0 && (
        <Box component='form' onSubmit={handleSingleSubmit}>
          <Box className='col-count-1 gap-3'>
            <TextField
              label='Shortcode'
              value={transKey}
              onChange={(e) => setTransKey(e.target.value.replace(/[^\w]/gi, ''))}
              fullWidth
              disabled={loading}
              placeholder='e.g., welcome_message'
            />
            <TextField
              label='Translation'
              value={transValue}
              onChange={(e) => setTransValue(e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={loading}
              placeholder='e.g., Welcome to our application'
            />
            <Box>
              <Button
                type='submit'
                variant='contained'
                startIcon={!loading && <MdOutlineSaveAlt />}
                disabled={loading}
                sx={{ alignSelf: 'flex-start' }}
              >
                {loading ? <CircularProgress size={24} /> : 'Save'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* CSV Upload Tab */}
      {activeTab === 1 && (
        <Box component='form' onSubmit={handleCSVSubmit}>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ display: 'block', mb: 2 }}
          >
            CSV format: First column is the key, second column is the value. You can use
            comma or tab as separator.
          </Typography>
          <Stack spacing={2}>
            <Button
              variant='outlined'
              component='label'
              startIcon={<CloudUploadIcon />}
              disabled={loading}
              fullWidth
            >
              Choose CSV File
              <input
                type='file'
                hidden
                accept='.csv'
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    setCsvFile(e.target.files[0]);
                    setError('');
                    setSuccess('');
                  }
                }}
                disabled={loading}
              />
            </Button>
            {csvFile && (
              <Typography variant='body2'>
                Selected: <strong>{csvFile.name}</strong>
              </Typography>
            )}
            <Button
              type='submit'
              variant='contained'
              disabled={loading || !csvFile}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          </Stack>
        </Box>
      )}

      {/* JSON Upload Tab */}
      {activeTab === 2 && (
        <Box component='form' onSubmit={handleJSONSubmit}>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ display: 'block', mb: 2 }}
          >
            JSON format: Array of objects with <code>transKey</code> and
            <code>transValue</code> properties, or object with key-value pairs.
          </Typography>
          <Stack spacing={2}>
            <Button
              variant='outlined'
              component='label'
              startIcon={<CloudUploadIcon />}
              disabled={loading}
              fullWidth
            >
              Choose JSON File
              <input
                type='file'
                hidden
                accept='.json'
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    setJsonFile(e.target.files[0]);
                    setError('');
                    setSuccess('');
                  }
                }}
                disabled={loading}
              />
            </Button>
            {jsonFile && (
              <Typography variant='body2'>
                Selected: <strong>{jsonFile.name}</strong>
              </Typography>
            )}
            <Button
              type='submit'
              variant='contained'
              disabled={loading || !jsonFile}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Uploading...' : 'Upload JSON'}
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

TranslationPluginForm.propTypes = {
  languageId: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
};

export default TranslationPluginForm;
